import { HttpClient } from '@adapters/HttpClient';
import type { LocationStockBatchEntry } from '@modules/Locations/models/Location';

export interface LocationsApiAdapterParams {
  per_page?: number;
  page?: number;
  search?: string;
  order_by?: string;
  order_direction?: 'asc' | 'desc';
  /** Solo ubicaciones sin producto asignado (advanced_filters product_id IS NULL) */
  requireEmptyProduct?: boolean;
  [key: string]: any;
}

export interface LocationListItem {
  id: string;
  name: string;
  typeName: string;
  typeId: string;
  productId: string | null;
  stock?: number;
  stock_batches?: LocationStockBatchEntry[];
}

export interface PaginatedLocationsResult {
  items: LocationListItem[];
  hasMore: boolean;
  total: number;
}

export class LocationsApiAdapter {
  constructor(private httpClient: HttpClient) {}

  private mapLocation(loc: any): LocationListItem {
    return {
      id: String(loc.id),
      name: loc.code,
      typeName: loc.type?.name || '',
      typeId: loc.type?.id ? String(loc.type.id) : '',
      productId: loc.product_id != null ? String(loc.product_id) : null,
      stock: typeof loc.stock === 'number' ? loc.stock : undefined,
      stock_batches: Array.isArray(loc.stock_batches)
        ? loc.stock_batches.map((entry: any) => ({
            batch_id: entry.batch_id,
            product_id: entry.product_id,
            quantity: entry.quantity,
            batch: entry.batch
              ? {
                  id: entry.batch.id,
                  batch_number: entry.batch.batch_number,
                  expiry_date: entry.batch.expiry_date,
                }
              : undefined,
          }))
        : undefined,
    };
  }

  private buildQueryParams(params: LocationsApiAdapterParams = {}): Record<string, string> {
    const reservedKeys = new Set(['per_page', 'page', 'search', 'order_by', 'order_direction', 'requireEmptyProduct']);

    const queryObj: Record<string, string> = {
      per_page: String(params.per_page ?? 20),
      page: String(params.page ?? 1),
      order_by: params.order_by ?? 'id',
      order_direction: params.order_direction ?? 'desc',
    };

    if (params.search?.trim()) {
      queryObj.search = params.search.trim();
    }

    Object.entries(params).forEach(([key, value]) => {
      if (!reservedKeys.has(key) && value !== undefined && value !== null) {
        queryObj[key] = String(value);
      }
    });

    if (params.requireEmptyProduct) {
      queryObj['advanced_filters[filters][0][column]'] = 'product_id';
      queryObj['advanced_filters[filters][0][operator]'] = 'is_null';
      queryObj['advanced_filters[logic]'] = 'AND';
    }

    return queryObj;
  }

  async getPaginated(params: LocationsApiAdapterParams = {}): Promise<PaginatedLocationsResult> {
    const page = params.page ?? 1;
    const query = new URLSearchParams(this.buildQueryParams(params)).toString();
    const raw = await this.httpClient.get<any>(`/locations?${query}`, undefined, {}, page === 1);
    const list = Array.isArray(raw) ? raw : (raw.data ?? []);
    const unique = list.filter(
      (loc: any, idx: number, arr: any[]) => arr.findIndex((l) => String(l.id) === String(loc.id)) === idx,
    );
    const meta = raw.meta ?? {};
    const currentPage = meta.currentPage ?? meta.current_page ?? page;
    const lastPage = meta.lastPage ?? meta.last_page ?? currentPage;

    return {
      items: unique.map((loc: any) => this.mapLocation(loc)),
      hasMore: currentPage < lastPage,
      total: meta.total ?? unique.length,
    };
  }

  async getAll(params: LocationsApiAdapterParams = {}): Promise<LocationListItem[]> {
    const hasProductIds = 'product_ids' in params && params.product_ids != null && params.product_ids !== '';
    const result = await this.getPaginated({
      ...params,
      requireEmptyProduct: params.requireEmptyProduct ?? !hasProductIds,
      per_page: params.per_page ?? 1000,
      page: 1,
    });
    return result.items;
  }
}
